import { Box, CircularProgress, Flex } from '@chakra-ui/react';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { match } from 'ts-pattern';
import { IClientApiResponse } from '~/clientApi';
import { Creator } from '~/clientApi/creator.model';
import { ContentSearchPickerModal } from '~/components/ContentSearchPickerModal';
import {
  SuperCreatorEditModal,
  useSuperCreatorEditStore,
} from '~/components/SuperCreatorEditModal';
import { FEATURE_FLAGS } from '~/constants';
import { agencyRoutes } from '~/core/router/groups';
import { getMainPlatformData } from '~/core/utils';
import { useListAgencies } from '~/hooks/api/useListAgencies';
import { useListAgencyCreators } from '~/hooks/api/useListAgencyCreators';
import { CONTENT_SEARCH_PARAMS } from '~/hooks/useContentSearch';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { ManagePlatforms } from '~/screens/ManagePlatforms';
import { EditCreatorModal } from '~/screens/MyAgency';
import { AppliedAdvancedFilters } from '~/screens/MyAgency/components/AppliedFilters';
import { TalentFilters } from '~/screens/MyAgency/components/CreatorsFiltersDrawer';
import { FiltersDrawer } from '~/screens/MyAgency/components/FiltersDrawer';
import { ListView } from '~/screens/MyAgency/components/ListView';
import { StackView } from '~/screens/MyAgency/components/StackView';
import {
  VIEW_TYPE,
  useActiveView,
} from '~/screens/MyAgency/hooks/useActiveView';
import { useFilterSortFields } from '~/screens/MyAgency/hooks/useFilterSortFields';
import { useGroupFilters } from '~/screens/MyAgency/hooks/useGroupFilters';
import { useMakeAdvancedFiltersToString } from '~/screens/MyAgency/hooks/useMakeAdvancedFiltersToString';
import {
  useAdvancedFiltersStore,
  useCreatorsFiltersStore,
  useSelectCreatorContentStore,
} from '~/screens/MyAgency/store';
import { LAYOUT_WIDTH } from '~/styles/components';
import { ICreator } from '~/types';
import { convertPxToRems } from '~/utils-ts';

import { ManageWebLinks } from '../ManageWebLinks/ManageWebLinks';
import { HeaderView } from './components/HeaderView';
import { FloatingMultiselectTalent } from './components/HeaderView/FloatingMultiSelect';
import { useSavedListsStore } from './store/useSavedListsStore';

export const MyAgency: React.FC = () => {
  const { creator, isModalOpen, onModalClose } = useSelectCreatorContentStore();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { activeView, setActiveView } = useActiveView();
  const slug = router.query['agency'] as string;

  const { creators: savedCreators, clearStore } = useSavedListsStore();

  const displaySaveListButton = useMemo(
    () => savedCreators.length > 0,
    [savedCreators],
  );

  const isShared = router.pathname === '/shared/[agency]';

  const cardViewFeatureFlag = useFeatureFlag(FEATURE_FLAGS.foamCollapsedView);

  const isCardViewEnabled = cardViewFeatureFlag === 'enabled' || isShared;

  const creatorListingFeatureFlag = useFeatureFlag(
    FEATURE_FLAGS.foamCreatorListingPage,
  );

  const isCreatorListingPageEnabled =
    creatorListingFeatureFlag === 'enabled' || isShared;

  const { appliedFilters } = useAdvancedFiltersStore();

  // use useFilterSortFields hook for toggling sort direction
  const {
    sortField,
    sortDirection,
    toggleOrder,
    isFieldSorted,
    setSortingOrder,
  } = useFilterSortFields({});

  useEffect(
    () => () => useSuperCreatorEditStore.getState().closeSuperEditModal(),
    [],
  );

  useEffect(() => {
    if (
      isShared ||
      activeView !== VIEW_TYPE.card ||
      cardViewFeatureFlag === 'loading'
    )
      return;
    setActiveView(isCardViewEnabled ? VIEW_TYPE.card : VIEW_TYPE.stacked);
  }, [
    setActiveView,
    isShared,
    activeView,
    cardViewFeatureFlag,
    isCardViewEnabled,
  ]);

  const { appliedFilters: appliedCreatorFilters } = useCreatorsFiltersStore();

  const appliedFiltersGrouped = useGroupFilters(appliedFilters);
  const convertAdvanceFiltersToString = useMakeAdvancedFiltersToString(
    appliedFiltersGrouped,
  );

  const allFilters = [
    ...convertAdvanceFiltersToString,
    ...appliedCreatorFilters,
  ];

  const {
    data: agencyData,
    error,
    fetchNextPage,
    hasNextPage,
    infiniteQueryKey,
    isFetchingNextPage,
    isInitialFetch,
    isLoading,
    totalCreators,
    hasFinishedLoadingCreators,
  } = useListAgencyCreators({
    sortField,
    sortDirection,
    slug,
    filters: allFilters,
  });
  const currentAgency = useListAgencies(slug);

  let doesNotHaveHandles = useRef(false);

  const creators = useMemo(() => {
    return agencyData.filter((data) => {
      const { handle } = getMainPlatformData(data);

      const { isOffPlatform } = data;
      // We filter out creators that don't have any handles because we consider there has been some issue in the BE trying to retrieve the data
      if (!handle) {
        doesNotHaveHandles.current = true;
        return false;
      }

      if (isShared && slug === 'whalartalent' && isOffPlatform) {
        // Do not show off platform creators for whalartalent in shared view
        return false;
      }

      return true;
    });
  }, [agencyData, slug, isShared]);

  const [isSticky, setIsSticky] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  const stickyProps = {
    ref: headerRef,
    top: 0,
    left: 0,
    position: isSticky ? 'fixed' : 'relative',
    zIndex: isSticky ? 'sticky' : 'inherit',
    background: 'white',
    width: '100%', // 1296 is the max width specified in the MyAgency/Layout file
  };
  const BoxWrapperProps = activeView ? stickyProps : {};

  const handleCreatorContentSuccess = (updatedCreator: ICreator) => {
    const data: InfiniteData<IClientApiResponse<Creator>> | undefined =
      queryClient.getQueryData(infiniteQueryKey);
    const updatedPages =
      data &&
      [...data.pages].map((page) => ({
        ...page,
        results: page.results.map((creator) =>
          creator.id === updatedCreator.id ? updatedCreator : creator,
        ),
      }));

    queryClient.setQueryData(infiniteQueryKey, {
      ...data,
      pages: updatedPages,
    });
    onModalClose();
  };

  useEffect(() => {
    const handleScroll = () => {
      if (
        headerRef.current &&
        headerRef.current.getBoundingClientRect().top <= 18 &&
        window.scrollY > 56 // this number is ideal for both my-agency and shared view
      ) {
        setIsSticky(true);
        return;
      }
      setIsSticky(false);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearStore();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [clearStore]);

  if (
    creatorListingFeatureFlag !== 'loading' &&
    !isCreatorListingPageEnabled &&
    !isInitialFetch
  )
    router.push(agencyRoutes.agencyTalent);

  if (isCreatorListingPageEnabled) {
    return (
      <>
        {/* Mobile and small screen view */}
        <Box display={{ base: 'block', md: 'none' }}>
          <Box paddingBottom="spacing-400">
            <HeaderView
              agencyData={creators}
              // typescript v5 update issue
              logo={(currentAgency.data as any)?.results.at(0).agencyLogo}
              isShared={isShared}
              // typescript v5 update issue
              title={(currentAgency.data as any)?.results.at(0).name}
              isAgencyEnabled={
                // typescript v5 update issue
                (currentAgency.data as any)?.results.at(0).enabled ?? false
              }
              enabledControls={
                // typescript v5 update issue
                (currentAgency.data as any)?.results?.at(0)?.enabledControls
              }
              sortField={sortField}
              sortDirection={sortDirection}
              setSortingOrder={setSortingOrder}
              displaySaveListButton={displaySaveListButton}
              totalCreators={totalCreators}
              hasFilters={allFilters.length > 0}
            />
            <AppliedAdvancedFilters
              isShared={isShared}
              filters={appliedFiltersGrouped}
              enabledControls={
                (currentAgency.data as any)?.results?.at(0)?.enabledControls
              }
            />
          </Box>
          {match(activeView)
            .with('list', () => (
              <ListView
                toggleOrder={toggleOrder}
                isFieldSorted={isFieldSorted}
                agencyData={creators}
                error={error}
                hasNextPage={hasNextPage}
                isInitialFetch={isInitialFetch}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                isShared={isShared}
                isLoading={isLoading}
              />
            ))
            .otherwise(() => (
              <StackView
                agencyData={creators}
                error={error}
                hasNextPage={hasNextPage}
                isInitialFetch={isInitialFetch}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                isLoading={isLoading}
                isShared={isShared}
                withContent={activeView === VIEW_TYPE.stacked}
                showMediaKitBtn={isShared}
              />
            ))}
        </Box>
        {/* Desktop and medium screen view */}
        <Box display={{ base: 'none', md: 'block' }} paddingBottom={10}>
          <Box {...BoxWrapperProps}>
            <Box
              maxW={activeView === 'list' ? '100%' : LAYOUT_WIDTH.restricted}
              paddingTop={isSticky ? convertPxToRems(12) : 0}
              paddingX={isSticky ? 'spacing-300' : 0}
              marginX="auto"
            >
              <HeaderView
                agencyData={creators}
                // typescript v5 update issue
                logo={(currentAgency.data as any)?.results.at(0).agencyLogo}
                isShared={isShared}
                // typescript v5 update issue
                title={(currentAgency.data as any)?.results.at(0).name}
                sortField={sortField}
                sortDirection={sortDirection}
                setSortingOrder={setSortingOrder}
                isAgencyEnabled={
                  // typescript v5 update issue
                  (currentAgency.data as any)?.results.at(0).enabled ?? false
                }
                enabledControls={
                  // typescript v5 update issue
                  (currentAgency.data as any)?.results?.at(0)?.enabledControls
                }
                displaySaveListButton={displaySaveListButton}
                totalCreators={totalCreators}
                hasFilters={allFilters.length > 0}
              />
              <AppliedAdvancedFilters
                isShared={isShared}
                filters={appliedFiltersGrouped}
                enabledControls={
                  (currentAgency.data as any)?.results?.at(0)?.enabledControls
                }
              />
            </Box>
          </Box>
          {match(activeView)
            .with('list', () => (
              <ListView
                toggleOrder={toggleOrder}
                isFieldSorted={isFieldSorted}
                agencyData={creators}
                error={error}
                hasNextPage={hasNextPage}
                isInitialFetch={isInitialFetch}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                isShared={isShared}
                isLoading={isLoading}
              />
            ))
            .otherwise(() => (
              <Box
                paddingTop={
                  isSticky
                    ? convertPxToRems(
                        (headerRef.current?.offsetHeight || 40) + 32,
                      )
                    : '0'
                }
              >
                <StackView
                  agencyData={creators}
                  error={error}
                  hasNextPage={hasNextPage}
                  isInitialFetch={isInitialFetch}
                  isFetchingNextPage={isFetchingNextPage}
                  fetchNextPage={fetchNextPage}
                  isLoading={isLoading}
                  isShared={isShared}
                  withContent={activeView === VIEW_TYPE.stacked}
                  showMediaKitBtn={isShared} // Show media kit button in shared view
                />
              </Box>
            ))}
          <Flex
            alignItems="center"
            // eslint-disable-next-line tailwindcss/no-custom-classname
            className="loader"
            justifyContent="center"
          >
            {hasNextPage ? (
              <CircularProgress
                aria-label="Loading more"
                color="primary.200"
                isIndeterminate
                paddingBottom="10px"
                size={6}
                trackColor="transparent"
              />
            ) : null}
          </Flex>
        </Box>
        {/* Sidebars */}
        <TalentFilters />
        <EditCreatorModal />
        <SuperCreatorEditModal />
        <FiltersDrawer />

        {creator && (
          <ContentSearchPickerModal
            creator={creator}
            isOpen={isModalOpen}
            onClose={onModalClose}
            onSuccess={handleCreatorContentSuccess}
            trackIds={{
              [CONTENT_SEARCH_PARAMS.ads]:
                'foam-myagency-stackview-edit-content-adtoggle-on',
              [CONTENT_SEARCH_PARAMS.platform]:
                'foam-myagency-stackview-edit-content-platform',
              [CONTENT_SEARCH_PARAMS.sortBy]:
                'foam-myagency-stackview-edit-content-sort',
              [CONTENT_SEARCH_PARAMS.query]:
                'foam-myagency-stackview-edit-content-query',
            }}
          />
        )}
        <ManagePlatforms />
        <ManageWebLinks />
        <FloatingMultiselectTalent
          creators={creators}
          hasFinishedLoading={hasFinishedLoadingCreators}
        />
      </>
    );
  }

  return null;
};
